import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import type { StorefrontItemBody } from '@/api/menu-list/storefront-menu-snapshot.types';
import { applyParentFallbacks } from '@/shared/lib/apply-parent-fallbacks';
import { computeAddonDefaults, type AddonSelections, type ItemsMap } from './compute-addon-defaults';
import type { FormValue } from './provider';

export type AddonsByVariant = Record<string, AddonSelections>;

type Params = {
    parentHash: string;
    parentItem: StorefrontItemBody;
    itemsMap: ItemsMap;
    isChangingByURL: boolean;
};

type SwitchResult = {
    switchVariant: (newVariant: string) => void;
};

const resolveVariantItem = (
    variantHash: string,
    parentHash: string,
    parentItem: StorefrontItemBody,
    itemsMap: ItemsMap,
): StorefrontItemBody | null => {
    if (variantHash === parentHash) return parentItem;
    const variantItem = itemsMap[variantHash]?.body;
    if (!variantItem) return null;
    return applyParentFallbacks(variantItem, parentItem);
};

export const useVariantSwitcher = ({
    parentHash,
    parentItem,
    itemsMap,
    isChangingByURL,
}: Params): SwitchResult => {
    const formMethods = useFormContext<FormValue>();
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const previousVariantRef = useRef<string | undefined>(formMethods.getValues('variant'));
    const addonsByVariantRef = useRef<AddonsByVariant>({});
    const isSeededRef = useRef(false);
    if (!isSeededRef.current) {
        isSeededRef.current = true;
        const initialVariant = formMethods.getValues('variant');
        const initialAddons = formMethods.getValues('addons');
        if (initialVariant && initialAddons) {
            addonsByVariantRef.current = { [initialVariant]: initialAddons };
        }
    }

    const lastSyncedUrlVariantRef = useRef<string | null | undefined>(undefined);

    const applyVariant = useCallback(
        (newVariant: string) => {
            const previousVariant = previousVariantRef.current;
            const currentAddons = formMethods.getValues('addons') ?? {};

            const newItem = resolveVariantItem(newVariant, parentHash, parentItem, itemsMap);
            if (!newItem) {
                if (process.env.NODE_ENV !== 'production') {
                    // eslint-disable-next-line no-console
                    console.warn(`useVariantSwitcher: variant ${newVariant} not found in itemsMap`);
                }
                return;
            }

            const prev = addonsByVariantRef.current;
            const draft: AddonsByVariant =
                previousVariant && previousVariant !== newVariant
                    ? { ...prev, [previousVariant]: currentAddons }
                    : { ...prev };
            const nextAddons: AddonSelections = draft[newVariant] ?? computeAddonDefaults(newItem, itemsMap);
            draft[newVariant] = nextAddons;

            addonsByVariantRef.current = draft;
            previousVariantRef.current = newVariant;

            formMethods.setValue('variant', newVariant);
            formMethods.setValue('addons', nextAddons);
        },
        [formMethods, parentHash, parentItem, itemsMap],
    );

    const switchVariant = useCallback(
        (newVariant: string) => {
            applyVariant(newVariant);

            if (isChangingByURL) {
                const params = new URLSearchParams(searchParams ?? undefined);
                params.set('variant', newVariant);
                lastSyncedUrlVariantRef.current = newVariant;
                router.replace(`${pathname}?${params.toString()}`, { scroll: false });
            }
        },
        [applyVariant, isChangingByURL, pathname, router, searchParams],
    );

    useEffect(() => {
        if (!isChangingByURL) return;
        const urlVariant = searchParams?.get('variant') ?? null;

        if (urlVariant === lastSyncedUrlVariantRef.current) return;
        lastSyncedUrlVariantRef.current = urlVariant;

        if (!urlVariant) {
            if (formMethods.getValues('variant') !== parentHash) {
                applyVariant(parentHash);
            }
            return;
        }

        if (urlVariant === parentHash) return;

        if (itemsMap[urlVariant]?.body) {
            if (urlVariant !== formMethods.getValues('variant')) {
                applyVariant(urlVariant);
            }
            return;
        }

        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(`useVariantSwitcher: invalid variant "${urlVariant}" in URL, cleaning up`);
        }
        if (formMethods.getValues('variant') !== parentHash) {
            applyVariant(parentHash);
        }
        const params = new URLSearchParams(searchParams ?? undefined);
        params.delete('variant');
        const queryString = params.toString();
        lastSyncedUrlVariantRef.current = null;
        router.replace(`${pathname}${queryString ? `?${queryString}` : ''}`, { scroll: false });
    }, [searchParams, isChangingByURL, parentHash, itemsMap, applyVariant, formMethods, router, pathname]);

    return { switchVariant };
};
